import { ElementRef, ViewChild, Input, EventEmitter, Output, Directive } from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { Utils } from '../utils';
import { FieldErrorComponent } from "./field-error/field-error.component";

@Directive()
export class InputField {
    /**
     * Reference to Utils class so it can be used in view.
     */
    Utils = Utils;
    /**
     * Control that accepts user input
     */
    @ViewChild('input') protected input: ElementRef | undefined;

    /**
     * Error field
     */
    @ViewChild('error', { static: true }) error: FieldErrorComponent | undefined;

    /**
     * Field label text. This property is optional.
     */
    @Input() label: string | undefined = "";
    /**
     * A unique elementId for this field. The elementId passes on to label to 
     * know which form element the label is bound to. This property is optional.
     */
    @Input() elementId: string = "";
    /**
     * Placeholder to be displayed when no user-provided text is in the input field.
     * This property is optional.
     */
    @Input() placeholder: string = "";
    /**
     * The maximum width (in rems) that this field should occupy.
     * This property is optional.
     */
    @Input() maxWidth: string = "";
    /**
     * The minimum width (in rems) that this field should occupy.
     */
    @Input() minWidth: string = "";
    /**
     * Whether to stretch height of text field to 100% height
     */
    @Input() stretchHeight: boolean = false;
    /**
     * Whether the form field is placed on a medium background as opposed to the default light background. Default is false, for light background.
     */
    @Input() onMediumBackground: boolean = false;

    /**
     * Whether the placeholder should be bolded. Placeholders should not be bolded when there are many
     * fields together as it would show too much emphasis on the page.
     */
    @Input() boldPlaceholder: boolean = false;

    /**
     * Hover tip to show when mouse is hovers over the label.
     */
    @Input() hoverTip: string | undefined = "";

    /**
     * Show the required indication on field.
     * This does not add the validator, it only adjusts the UI in cases
     * where the field style does not show even though the validator is properly
     * on the field. Be sure to add the "required" validator on the field. 
     * Use when data is set on the field before the field displayed.
     */
    @Input() showRequiredIndication: boolean | undefined = false;

    /**
     * Whether the form field is read-only.
     */
    @Input() readOnly: boolean | undefined = false;

    /**
     * The event that should fire when the field has focus.
     * This property is optional.
     */
    @Output() onFieldFocus = new EventEmitter();
    /**
     * The event that should fire when the field is blurred (leaves focus).
     * This property is optional.
     */
    @Output() onFieldBlur = new EventEmitter();
    /**
     * The event that should fire when the field submits.
     * This property is optional.
     */
    @Output() onFieldSubmit = new EventEmitter();
    /**
     * The form group of the entire input field form.
     */
    form: FormGroup;
    /**
     * Fire onChange when data has been changed in the form to alert the parent form.
     * This is used by the Angular Reactive Forms framework.
     */
    onChange: ((value: any) => void) | undefined;

    /**
     * Fire onTouched when field has been touched in the form to alert the parent form.
     * This is used by the Angular Reactive Forms framework.
     */
    onTouched: ((value: any) => void) | undefined;
    /**
     * Create an input-field object.
     */
    constructor() {
        this.form = new FormGroup({
            'field': new FormControl()
        }
        );
    }
    /**
     * Set focus on the input field element.
     */
    setFocus() {
        if (this.input && this.input.nativeElement) {
            this.input.nativeElement.focus();
        }
    }
    /**
     * Get the form control for the input field.
     */
    get field(): FormControl {
        return this.form.get("field") as FormControl;
    }
    /**
     * Raise event to the parent form that the field has focus.
     */
    onFocus() {
        this.onFieldFocus.emit();
    }
    /**
     * Raise event to the parent form that the field has blurred (lost focus).
     */
    onBlur() {
        if (this.onTouched != undefined) {
            this.onTouched(undefined);
        }
        this.onFieldBlur.emit();
    }

    /**
     * Raise event to the parent form that the value has changed and include the value.
     */
    valueChanged(value: any) {
        if (this.onChange) {
            this.onChange(value);
        }
    }

    /**
     * Raise event to the parent form that the field has submitted -- usually when the ENTER key pressed.
     */
    fieldFormSubmit() {
        if (this.form.valid) {
            this.onFieldSubmit.emit();
        }
    }
    /**
     * Get the wrapper class, in ngClass format, for the field.
     */
    wrapperClass() {
        //
        // Include margins when the field control has a label.
        // Fields with a label are typically used in a form with
        // other form controls. Fields without a label are typically
        // used inline and should not have no margins.
        //
        // When stretching height to 100%, apply the ot-stretch-height style to the field.
        //
        return {
            "ot-field-margins": this.label !== undefined && this.label.length > 0,
            "ot-stretch-height": this.stretchHeight,
            "ot-on-medium-background": this.onMediumBackground,
            "ot-bold-placeholder": this.boldPlaceholder,
            "ot-field-error": this.error !== undefined && this.error.isError
        };
    }

    /**
     * Get whether the value is empty (i.e. null, undefined, or empty string)
     */
    isValueEmpty(value: any) {
        return value === undefined || value === null || value === '';
    }
}

